import { useState } from 'react';

import type { ChannelLimits, ChannelUsageDto } from '../../api/types';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

export interface ChannelLimitsFormProps {
  limits: ChannelLimits;
  /** Current window/day/week counters, shown next to the caps they belong to. */
  usage?: ChannelUsageDto;
  onSave: (limits: ChannelLimits) => void;
  onCancel: () => void;
  pending?: boolean;
  error?: string | null;
}

interface FieldSpec {
  key: keyof ChannelLimits;
  label: string;
  hint: string;
  step?: string;
  /** Optional caps are cleared with an empty input instead of typing `null`. */
  nullable?: boolean;
}

/** U5.4: named fields with plain-language labels, instead of editing raw JSON. */
const FIELDS: readonly FieldSpec[] = [
  { key: 'window_hours', label: 'Окно, часов', hint: 'За какой отрезок времени считается лимит. Обычно 4 часа.' },
  { key: 'window_max', label: 'Сообщений в окне', hint: 'Сколько сообщений агент отправит за это окно.' },
  { key: 'daily_max', label: 'Не больше в сутки', hint: 'Пусто — без суточного ограничения.', nullable: true },
  { key: 'weekly_max', label: 'Не больше в неделю', hint: 'Пусто — без недельного ограничения.', nullable: true },
  { key: 'warmup_days', label: 'Дни прогрева', hint: 'Сколько дней после подключения действует пониженный лимит.' },
  { key: 'warmup_factor', label: 'Коэффициент прогрева', hint: 'Во сколько раз режутся лимиты во время прогрева: 0.3 = 30% от обычного.', step: '0.05' },
];

function toInput(value: number | null): string {
  return value === null ? '' : String(value);
}

export function ChannelLimitsForm({ limits, usage, onSave, onCancel, pending, error }: ChannelLimitsFormProps) {
  const [draft, setDraft] = useState<Record<keyof ChannelLimits, string>>({
    window_hours: toInput(limits.window_hours),
    window_max: toInput(limits.window_max),
    daily_max: toInput(limits.daily_max),
    weekly_max: toInput(limits.weekly_max),
    warmup_days: toInput(limits.warmup_days),
    warmup_factor: toInput(limits.warmup_factor),
  });
  const [localError, setLocalError] = useState<string | null>(null);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const parsed: Record<string, number | null> = {};
    for (const f of FIELDS) {
      const raw = draft[f.key].trim();
      if (raw === '') {
        if (!f.nullable) {
          setLocalError(`Поле «${f.label}» обязательно`);
          return;
        }
        parsed[f.key] = null;
        continue;
      }
      const n = Number(raw);
      if (!Number.isFinite(n)) {
        setLocalError(`Поле «${f.label}»: введите число`);
        return;
      }
      parsed[f.key] = n;
    }
    setLocalError(null);
    // Every field in FIELDS is set above, so the record is a complete ChannelLimits.
    onSave(parsed as unknown as ChannelLimits);
  }

  const counter = (prefix: string, c: { used: number; max: number | null }): string =>
    `${prefix} ${c.used}${c.max === null ? '' : ` из ${c.max}`}`;
  const used: Partial<Record<keyof ChannelLimits, string>> = usage
    ? {
        window_max: counter('сейчас использовано', usage.window),
        daily_max: counter('сегодня', usage.daily),
        weekly_max: counter('за неделю', usage.weekly),
      }
    : {};

  return (
    <form onSubmit={submit} aria-label="Лимиты канала" className="w-72 space-y-3">
      {FIELDS.map((f) => (
        <div key={f.key}>
          <label className="block text-xs font-medium" htmlFor={`limit-${f.key}`}>
            {f.label}
          </label>
          <Input
            id={`limit-${f.key}`}
            type="number"
            step={f.step}
            className="w-full"
            value={draft[f.key]}
            onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
          />
          <p className="mt-0.5 text-[11px] leading-snug text-muted-fg">{f.hint}</p>
          {used[f.key] && <p className="text-[11px] font-medium text-primary">{used[f.key]}</p>}
        </div>
      ))}
      {usage?.warmup && (
        <p className="rounded bg-muted p-2 text-[11px] text-muted-fg">
          Канал ещё на прогреве — лимиты действуют с коэффициентом до{' '}
          {new Date(usage.warmupEndsAt).toLocaleDateString('ru-RU')}.
        </p>
      )}
      {(localError ?? error) && <p role="alert" className="text-xs text-red-600">{localError ?? error}</p>}
      <div className="flex gap-1">
        <Button size="sm" type="submit" disabled={pending}>
          Сохранить
        </Button>
        <Button size="sm" type="button" variant="secondary" onClick={onCancel}>
          Отмена
        </Button>
      </div>
    </form>
  );
}
